import Immutable from "immutable";

import { uuid } from "services/generate-uuid";
import { TypedMap } from "types/global";
import { getResource } from "selectors/resources";
import { Features } from "resourceModels/Client";
import BaseMessageRecord, {
  BaseMessageRecordAttributes,
  BaseMessageRecordInstance,
  BlockConfigGetter
} from "../BaseMessageRecord";

interface RecordAttributes extends BaseMessageRecordAttributes {
  type?: "conditionals_block";
  statements: Immutable.List<ConditionalStatement>
}

const blockConfig: BlockConfigGetter = (state) => {
  const client = getResource(state, "client");

  return ({
    isVisible: (
      !client.hasFeature(Features.ZENDESK_CLIENT) &&
        client.hasFeature(Features.PERSONALIZATION) &&
        client.hasFeature(Features.SEGMENTATION)
    ),
    blockType: "operation",
    blockName: client.hasFeature(Features.CONDITIONALS) ? "Conditional" : "Segmentation",
    blockTitle: client.hasFeature(Features.CONDITIONALS) ? "Conditional" : "Segmentation",
    shortTitle: client.hasFeature(Features.CONDITIONALS) ? "Conditional" : "Segmentation",
    icon: "Segmentation"
  });
};

export class ConditionalMessageRecord extends BaseMessageRecord<RecordAttributes>({
  type: "conditionals_block",
  statements: Immutable.List()
}) {
  static blockConfig = blockConfig;

  /**
   * Validation
   */

  static getInvalidFields(message: ConditionalMessageRecord): Immutable.List<string> {
    const invalidFields: Set<string> = new Set();

    // Get invalid fields from nested messages
    // For each statement
    message.statements.forEach(
      (
        statement,
        statementIndex
      ) => {
        // For each message
        statement.messages.forEach((m, messageIndex) => {
          // For each invalid field
          m.invalidFields.forEach((fieldName) => {
            invalidFields.add(`statement-${statementIndex}-message-${messageIndex}-${fieldName}`);
          });
        });

        if (statementIndex + 1 !== message.statements.size) {
          statement.conditions.forEach(
            /**
             * @param {ConditionalRecord} conditionalRecord
             * @param {Number} conditionalRecordIndex
             */
            (conditionalRecord, conditionalRecordIndex) => {
              if (!conditionalRecord.leftOperand) {
                invalidFields.add(`${statementIndex}-${conditionalRecordIndex}-leftOperand`);
              }

              if (!conditionalRecord.comparisonOperator.length) {
                invalidFields.add(`${statementIndex}-${conditionalRecordIndex}-comparisonOperator`);
              }
            }
          );
        }
      }
    );

    return Immutable.List(invalidFields);
  }
}

export class ConditionalRecord extends Immutable.Record({
  id: null,
  leftOperand: null,
  comparisonOperator: "is",
  rightOperand: "",
  fieldType: "string",
  matchCase: false
}) {}

interface ConditionalStatementAttributes {
  operator: string;
  conditions: Immutable.List<ConditionalRecord>;
  messages: Immutable.List<BaseMessageRecordInstance>;
}

export class ConditionalStatement extends Immutable.Record<ConditionalStatementAttributes>({
  operator: "AND",
  conditions: Immutable.List([
    new ConditionalRecord({ id: uuid() })
  ]),
  messages: Immutable.List()
}) {}

/**
 * Creates ConditionalMessageRecord, using `messageCreator` to create nested messages
 */
export default function ConditionalMessageRecordCreator(
  attributes: unknown,
  messageCreator: (
    message: unknown,
    shouldMessageTransformFromAPI: boolean
  ) => BaseMessageRecordInstance,
  shouldMessageTransformFromAPI: boolean
): ConditionalMessageRecord {
  const attrsMap: TypedMap<{
    statements?: Immutable.List<ConditionalStatement>
  }> = Immutable.fromJS(attributes);

  return new ConditionalMessageRecord({
    ...attrsMap.toJS(),

    statements: attrsMap.get("statements")
      ? attrsMap.get("statements").map(statement => new ConditionalStatement({
        operator: statement.get("operator"),
        conditions: statement.get("conditions").map(
          condition => new ConditionalRecord(
            condition.merge({ id: uuid() })
          )
        ),
        messages: statement.get("messages")
          ? statement.get("messages").map(m => messageCreator(m, shouldMessageTransformFromAPI))
          : Immutable.List()
      }))
      : Immutable.List([
        new ConditionalStatement(),
        new ConditionalStatement()
      ])
  });
}

ConditionalMessageRecordCreator.blockConfig = blockConfig;

